import { MergeAttractionTypeDialog } from '@/components/attraction-types/MergeAttractionTypeDialog';
import type { AttractionType } from '@/config/objects/attraction-types';
import {
  Breadcrumbs,
  Button,
  ConfirmDialog,
  DetailPageHeader,
  DetailPageHero,
  PageContainer,
  useApiClient,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GitMerge, MapPin, Pencil, Tag, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from '@startupquickstart/frontend';

const tabs = [
  { label: 'Overview', path: 'overview', icon: MapPin },
  { label: 'Attractions', path: 'attractions', icon: MapPin },
  { label: 'Categories', path: 'categories', icon: Tag },
];

export function AttractionTypeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMergeDialog, setShowMergeDialog] = useState(false);

  const { data, isLoading } = useQuery<{ success: boolean; data: AttractionType }>({
    queryKey: ['AttractionType', id],
    queryFn: () => apiClient.get(`/attraction-types/${id}`),
    enabled: !!id,
  });

  const attractionType = data?.data;

  const deleteMutation = useMutation({
    mutationFn: async (attractionTypeId: string): Promise<{ success: boolean; error?: string }> => {
      return apiClient.delete(`/attraction-types/${attractionTypeId}`);
    },
    onSuccess: (response: { success: boolean; error?: string }) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['AttractionType'] });
        navigate('/attraction-types');
      } else {
        throw new Error(response.error || 'Failed to delete attraction type');
      }
    },
  });

  const handleDelete = () => {
    if (!attractionType) return;
    toast.promise(deleteMutation.mutateAsync(attractionType.id), {
      loading: 'Deleting attraction type...',
      success: 'Attraction type deleted successfully',
      error: (error: Error) => error?.message || 'Failed to delete attraction type',
    });
  };

  const mergeMutation = useMutation({
    mutationFn: async (
      targetTypeId: string,
    ): Promise<{ success: boolean; data?: { movedCount: number }; error?: string }> => {
      return apiClient.post(`/attraction-types/${id}/merge`, { targetTypeId });
    },
    onSuccess: (response, targetTypeId) => {
      if (!response.success) {
        throw new Error(response.error || 'Failed to merge attraction type');
      }
      // Both the type taxonomy and the affected attractions changed.
      queryClient.invalidateQueries({ queryKey: ['AttractionType'] });
      queryClient.invalidateQueries({ queryKey: ['Attraction'] });
      setShowMergeDialog(false);
      navigate(`/attraction-types/${targetTypeId}`);
    },
  });

  const handleMerge = (targetTypeId: string) => {
    toast.promise(mergeMutation.mutateAsync(targetTypeId), {
      loading: 'Merging attraction type...',
      success: 'Attraction type merged successfully',
      error: (error: Error) => error?.message || 'Failed to merge attraction type',
    });
  };

  useEffect(() => {
    if (id) {
      const basePath = `/attraction-types/${id}`;
      if (location.pathname === basePath || location.pathname === `${basePath}/`) {
        navigate(`${basePath}/overview`, { replace: true });
      }
    }
  }, [id, location.pathname, navigate]);

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Attraction Types', href: '/attraction-types' },
    { label: attractionType?.name || 'Attraction Type', current: true },
  ];

  const basePath = `/attraction-types/${id}`;
  const imageUrl = attractionType?.featuredImage?.url ?? null;

  if (!id) {
    return (
      <PageContainer>
        <div className="text-center">
          <h2 className="text-lg font-medium mb-2">Attraction Type Not Found</h2>
          <p className="text-muted-foreground">
            The requested attraction type could not be found.
          </p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer padding="none" className="flex flex-col overflow-hidden">
      <DetailPageHeader
        actions={
          attractionType && (
            <>
              <Button variant="outline" onClick={() => setShowMergeDialog(true)}>
                <GitMerge className="h-4 w-4" />
                Merge into…
              </Button>
              <Button variant="outline" onClick={() => navigate(`${basePath}/edit`)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          )
        }
      >
        <Breadcrumbs items={breadcrumbs} />
      </DetailPageHeader>

      <DetailPageHero
        isLoading={isLoading}
        category={{ label: 'Attraction Type', icon: MapPin }}
        title={attractionType?.name || 'Attraction Type'}
        image={{
          url: imageUrl,
          alt: attractionType?.name || 'Attraction Type',
          fallbackIcon: MapPin,
        }}
        tabs={tabs}
        basePath={basePath}
      >
        <Outlet context={{ attractionType, isLoading }} />
      </DetailPageHero>

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Attraction Type"
        description={`Are you sure you want to delete "${attractionType?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />

      <MergeAttractionTypeDialog
        open={showMergeDialog}
        onOpenChange={setShowMergeDialog}
        sourceName={attractionType?.name || 'this type'}
        loading={mergeMutation.isPending}
        onConfirm={handleMerge}
      />
    </PageContainer>
  );
}
